import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactFlowProvider } from '@xyflow/react'
import { routeTree } from './routeTree'
import { ThemeProvider } from '@/components/ThemeProvider'
import { initNativeShell } from '@/lib/nativePlatform'
import { initNativeAuth } from '@/lib/nativeAuth'
import { initAppZoom } from '@/lib/appZoom'
import { installNativeMidiPolyfill } from '@/lib/nativeMidi'
import '@/styles/globals.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60 * 1000, retry: 1 },
  },
})

const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Capacitor bootstrap: restore auth tokens from Keychain and wire up
// the status bar / splash screen / keyboard plugins before we mount
// React. Both functions no-op on the web build. We don't await them —
// the initial render can happen concurrently with the Keychain read,
// and `useAuth()` will refetch once the tokens are loaded (the
// QueryClient fetch runs after React commits).
// Install the CoreMIDI-backed `navigator.requestMIDIAccess` polyfill
// BEFORE React mounts — the midiStore reads `'requestMIDIAccess' in
// navigator` at module load to set its `supported` flag, so the shim
// has to be in place by then. No-op on web.
installNativeMidiPolyfill()
void initNativeAuth()
void initNativeShell()
initAppZoom()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ReactFlowProvider>
          <RouterProvider router={router} />
        </ReactFlowProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
)
